
import React, { useState } from "react";
import { PartCategory, PartSubcategory } from "@/data/parts";
import { ChevronRight } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface CategoryMenuProps {
  selectedCategory: PartCategory | "all";
  setSelectedCategory: (category: PartCategory | "all") => void;
  selectedSubcategory: PartSubcategory | "all";
  setSelectedSubcategory: (subcategory: PartSubcategory | "all") => void;
}

const CategoryMenu = ({ 
  selectedCategory, 
  setSelectedCategory, 
  selectedSubcategory, 
  setSelectedSubcategory 
}: CategoryMenuProps) => {
  // Track expanded state for each category
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    all: true,
    k1_series: false,
    k2_series: false,
    tools: false
  });

  // Toggle expand/collapse for a category
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  // Handle subcategory click with category context
  const handleSubcategoryClick = (category: PartCategory | "all", subcategory: PartSubcategory | "all") => {
    setSelectedCategory(category);
    setSelectedSubcategory(subcategory);
  };

  const menuItemClasses = "flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none hover:bg-sidebar-accent transition-colors";
  const activeMenuItemClasses = "bg-sidebar-accent font-medium";
  const subMenuItemClasses = "flex h-7 min-w-0 items-center gap-2 overflow-hidden rounded-md px-2 text-sm hover:bg-sidebar-accent transition-colors";
  const activeSubMenuItemClasses = "bg-sidebar-accent";

  return (
    <ul className="flex w-full min-w-0 flex-col gap-1">
      {/* All Models */}
      <li className="relative group">
        <Collapsible open={expandedCategories.all} onOpenChange={() => toggleCategory("all")}>
          <div 
            className={cn(
              menuItemClasses, 
              selectedCategory === "all" ? activeMenuItemClasses : ""
            )}
            onClick={() => setSelectedCategory("all")}
          >
            <CollapsibleTrigger asChild>
              <div className="flex items-center gap-2 cursor-pointer">
                <ChevronRight className={`h-4 w-4 transition-transform ${expandedCategories.all ? "rotate-90" : ""}`} />
                <span>All Models</span>
              </div>
            </CollapsibleTrigger>
          </div>
          <CollapsibleContent>
            <ul className="mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5">
              <li>
                <button 
                  className={cn(
                    subMenuItemClasses, 
                    selectedCategory === "all" && selectedSubcategory === "all" ? activeSubMenuItemClasses : ""
                  )}
                  onClick={() => handleSubcategoryClick("all", "all")}
                >
                  All Parts
                </button>
              </li>
              <li>
                <button 
                  className={cn(
                    subMenuItemClasses, 
                    selectedCategory === "all" && selectedSubcategory === "hotend" ? activeSubMenuItemClasses : ""
                  )}
                  onClick={() => handleSubcategoryClick("all", "hotend")}
                >
                  Hotend
                </button>
              </li>
              <li>
                <button 
                  className={cn(
                    subMenuItemClasses, 
                    selectedCategory === "all" && selectedSubcategory === "extruder" ? activeSubMenuItemClasses : ""
                  )}
                  onClick={() => handleSubcategoryClick("all", "extruder")}
                >
                  Extruder
                </button>
              </li>
              <li>
                <button 
                  className={cn(
                    subMenuItemClasses, 
                    selectedCategory === "all" && selectedSubcategory === "probe" ? activeSubMenuItemClasses : ""
                  )}
                  onClick={() => handleSubcategoryClick("all", "probe")}
                >
                  Probe
                </button>
              </li>
              <li>
                <button 
                  className={cn(
                    subMenuItemClasses, 
                    selectedCategory === "all" && selectedSubcategory === "fan" ? activeSubMenuItemClasses : ""
                  )}
                  onClick={() => handleSubcategoryClick("all", "fan")}
                >
                  Fan
                </button>
              </li>
            </ul>
          </CollapsibleContent>
        </Collapsible>
      </li>
      
      {/* K1 Series */}
      <li className="relative group">
        <Collapsible open={expandedCategories.k1_series} onOpenChange={() => toggleCategory("k1_series")}>
          <div 
            className={cn(
              menuItemClasses, 
              selectedCategory === "k1_series" ? activeMenuItemClasses : ""
            )}
            onClick={() => setSelectedCategory("k1_series")}
          >
            <CollapsibleTrigger asChild>
              <div className="flex items-center gap-2 cursor-pointer">
                <ChevronRight className={`h-4 w-4 transition-transform ${expandedCategories.k1_series ? "rotate-90" : ""}`} />
                <span>K1 Series</span>
              </div>
            </CollapsibleTrigger>
          </div>
          <CollapsibleContent>
            <ul className="mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5">
              <li>
                <button 
                  className={cn(
                    subMenuItemClasses, 
                    selectedCategory === "k1_series" && selectedSubcategory === "all" ? activeSubMenuItemClasses : ""
                  )}
                  onClick={() => handleSubcategoryClick("k1_series", "all")}
                >
                  All Parts
                </button>
              </li>
              <li>
                <button 
                  className={cn(
                    subMenuItemClasses, 
                    selectedCategory === "k1_series" && selectedSubcategory === "hotend" ? activeSubMenuItemClasses : ""
                  )}
                  onClick={() => handleSubcategoryClick("k1_series", "hotend")}
                >
                  Hotend
                </button>
              </li>
              <li>
                <button 
                  className={cn(
                    subMenuItemClasses, 
                    selectedCategory === "k1_series" && selectedSubcategory === "extruder" ? activeSubMenuItemClasses : ""
                  )}
                  onClick={() => handleSubcategoryClick("k1_series", "extruder")}
                >
                  Extruder
                </button>
              </li>
              <li>
                <button 
                  className={cn(
                    subMenuItemClasses, 
                    selectedCategory === "k1_series" && selectedSubcategory === "probe" ? activeSubMenuItemClasses : ""
                  )}
                  onClick={() => handleSubcategoryClick("k1_series", "probe")}
                >
                  Probe
                </button>
              </li>
              <li>
                <button 
                  className={cn(
                    subMenuItemClasses, 
                    selectedCategory === "k1_series" && selectedSubcategory === "fan" ? activeSubMenuItemClasses : ""
                  )}
                  onClick={() => handleSubcategoryClick("k1_series", "fan")}
                >
                  Fan
                </button>
              </li>
            </ul>
          </CollapsibleContent>
        </Collapsible>
      </li>
      
      {/* K2 Series */}
      <li className="relative group">
        <Collapsible open={expandedCategories.k2_series} onOpenChange={() => toggleCategory("k2_series")}>
          <div 
            className={cn(
              menuItemClasses, 
              selectedCategory === "k2_series" ? activeMenuItemClasses : ""
            )}
            onClick={() => setSelectedCategory("k2_series")}
          >
            <CollapsibleTrigger asChild>
              <div className="flex items-center gap-2 cursor-pointer">
                <ChevronRight className={`h-4 w-4 transition-transform ${expandedCategories.k2_series ? "rotate-90" : ""}`} />
                <span>K2 Series</span>
              </div>
            </CollapsibleTrigger>
          </div>
          <CollapsibleContent>
            <ul className="mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5">
              <li>
                <button 
                  className={cn(
                    subMenuItemClasses, 
                    selectedCategory === "k2_series" && selectedSubcategory === "all" ? activeSubMenuItemClasses : ""
                  )}
                  onClick={() => handleSubcategoryClick("k2_series", "all")}
                >
                  All Parts
                </button>
              </li>
              <li>
                <button 
                  className={cn(
                    subMenuItemClasses, 
                    selectedCategory === "k2_series" && selectedSubcategory === "hotend" ? activeSubMenuItemClasses : ""
                  )}
                  onClick={() => handleSubcategoryClick("k2_series", "hotend")}
                >
                  Hotend
                </button>
              </li>
              <li>
                <button 
                  className={cn(
                    subMenuItemClasses, 
                    selectedCategory === "k2_series" && selectedSubcategory === "extruder" ? activeSubMenuItemClasses : ""
                  )}
                  onClick={() => handleSubcategoryClick("k2_series", "extruder")}
                >
                  Extruder
                </button>
              </li>
              <li>
                <button 
                  className={cn(
                    subMenuItemClasses, 
                    selectedCategory === "k2_series" && selectedSubcategory === "probe" ? activeSubMenuItemClasses : ""
                  )}
                  onClick={() => handleSubcategoryClick("k2_series", "probe")}
                >
                  Probe
                </button>
              </li>
              <li>
                <button 
                  className={cn(
                    subMenuItemClasses, 
                    selectedCategory === "k2_series" && selectedSubcategory === "fan" ? activeSubMenuItemClasses : ""
                  )}
                  onClick={() => handleSubcategoryClick("k2_series", "fan")}
                >
                  Fan
                </button>
              </li>
            </ul>
          </CollapsibleContent>
        </Collapsible>
      </li>
      
      {/* Tools */}
      <li className="relative group">
        <button 
          className={cn(
            menuItemClasses, 
            selectedCategory === "tools" ? activeMenuItemClasses : ""
          )}
          onClick={() => handleSubcategoryClick("tools", "all")}
        >
          <span className="w-4 h-4"></span> {/* Spacer to align with other menu items */}
          <span>Tools</span>
        </button>
      </li>
    </ul>
  );
};

export default CategoryMenu;
